import { useEffect, useRef, useState } from 'react';

/**
 * Rising-edge delay: true only once `active` has stayed true for `delayMs`;
 * false the instant `active` drops.
 *
 * The asymmetry is the point. Delaying the rise suppresses indicators for work
 * that finishes quickly (a spinner that flashes for 40ms reads as a glitch, not
 * as feedback); dropping immediately means the indicator can never outlive the
 * thing it describes, so a stranded indicator isn't a case callers must handle.
 *
 * Caveat inherited from the rest of this directory (see _CONTEXT.md gotcha 1):
 * expiry is wall-clock approximate. The timer cannot fire while the main thread
 * is blocked, so under a long uninterruptible render the flag rises late — or,
 * if `active` has already gone false by then, not at all.
 */
export function useDelayedFlag(active: boolean, delayMs: number): boolean {
  const [elapsed, setElapsed] = useState(false);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    if (!active) {
      // Fall immediately, and cancel any rise still in flight.
      if (timerRef.current != null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      setElapsed(false);
      return;
    }
    // Re-arm on every rising edge. Clear first: an `active` that toggles faster
    // than delayMs must never stack timers, or an old one fires against a new
    // window and the flag latches true early.
    if (timerRef.current != null) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      setElapsed(true);
    }, delayMs);

    return () => {
      if (timerRef.current != null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [active, delayMs]);

  // Gate on `active` as well as the timer: on the falling edge this returns
  // false during the same render that schedules setElapsed(false), rather than
  // leaving the flag true for one extra frame.
  return active && elapsed;
}
